import { Box, Button, useClassNames } from '@factorialco/deprecated-design-system'
import { SettingsPayrollEmployeeGroupFragment } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import BoxList from 'modules/core/components/BoxList'
import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'
import type LegalEntity from 'modules/core/store/models/LegalEntity'

import ListItem from 'modules/settings/components/Payroll/LegalEntity/Employees/EmployeeGroups/_ListItem'

import './index.styles.css'

const stylesRefs = {
  title: 'EmployeeGroups_title',
  description: 'EmployeeGroups_description',
  negative: 'EmployeeGroups_negative',
}

interface Props {
  legalEntity: LegalEntity
  employeeGroups: SettingsPayrollEmployeeGroupFragment[]
  selectEmployeeGroup: (employeeGroupId: number) => void
  selectedEmployeeGroup?: SettingsPayrollEmployeeGroupFragment
}

const EmployeesGroups = ({
  legalEntity,
  employeeGroups,
  selectEmployeeGroup,
  selectedEmployeeGroup,
}: Props) => {
  const cx = useClassNames(stylesRefs)
  const { navigation } = useNavigation()

  const items = employeeGroups.map((group) => {
    const isHighlighted = selectedEmployeeGroup?.id === group.id
    const notTerminatedEmployees = group.employees.filter(
      (employee) => employee.terminatedOn === null
    )

    return (
      <ListItem
        onClick={() => selectEmployeeGroup(group.id)}
        key={`employee_group_${group.id}`}
        highlighted={isHighlighted}
      >
        <Box className={'EmployeeGroups_item'} flexDirection="row">
          <Box>
            <Box className={cx('title', { negative: isHighlighted })}>{group.name}</Box>
            <Box className={cx('description', { negative: isHighlighted })}>
              {i18n.tp('settings.legal_entities.legal_entity.employee_group.employees_count', {
                count: notTerminatedEmployees.length,
              })}
            </Box>
          </Box>
          <Box className={'EmployeeGroups_options'} flexDirection="row">
            <PopoverMenu type="list">
              <PopoverMenu.Trigger>
                <Button.Decorative
                  type={isHighlighted ? 'primary' : 'secondary'}
                  iconName="ellipsis"
                />
              </PopoverMenu.Trigger>
              <PopoverMenu.Content side="bottom" align="center">
                <PopoverMenu.RouterOption
                  title={i18n.t('settings.legal_entities.legal_entity.employee_group.edit.menu')}
                  to={{
                    pathname:
                      navigation.settings.legalEntitiesId.employeeGroups.employeeGroupId.edit.fullPath(
                        {
                          id: legalEntity.id,
                          employee_group_id: group.id,
                        }
                      ),
                  }}
                />
                {!group.main && (
                  <PopoverMenu.RouterOption
                    title={i18n.t(
                      'settings.legal_entities.legal_entity.employee_group.main.action'
                    )}
                    to={{
                      pathname:
                        navigation.settings.legalEntitiesId.employeeGroups.employeeGroupId.main.fullPath(
                          {
                            id: legalEntity.id,
                            employee_group_id: group.id,
                          }
                        ),
                    }}
                  />
                )}
                {employeeGroups.length > 1 && (
                  <PopoverMenu.RouterOption
                    title={i18n.t(
                      'settings.legal_entities.legal_entity.employee_group.remove.menu'
                    )}
                    to={{
                      pathname:
                        navigation.settings.legalEntitiesId.employeeGroups.employeeGroupId.remove.fullPath(
                          {
                            id: legalEntity.id,
                            employee_group_id: group.id,
                          }
                        ),
                    }}
                    danger
                  />
                )}
              </PopoverMenu.Content>
            </PopoverMenu>
            <Box className={'EmployeeGroups_forward'}>
              <Button
                type={isHighlighted ? 'primary' : 'secondary'}
                submit
                iconName="arrow-right"
              />
            </Box>
          </Box>
        </Box>
      </ListItem>
    )
  })

  return <BoxList>{items}</BoxList>
}

export default observer(EmployeesGroups)
